import React, { useState, useEffect } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl, InputLabel, InputAdornment, IconButton, Snackbar, Alert,
} from '@mui/material';
import { collection, doc, setDoc, query, where, onSnapshot, getDocs, deleteDoc, updateDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { styled } from "@mui/material/styles";
import Card from "@mui/material/Card";
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    boxShadow: theme.shadows[6],
  },
}));

const AddServiceArea = ({ companyCode }) => {
  const [serviceAreas, setServiceAreas] = useState([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newServiceArea, setNewServiceArea] = useState({
    serviceAreaName: '',
    priceCategory: '',
    unitCode: ''
  });
  const [units, setUnits] = useState([]);
  const [priceCategories, setPriceCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  // Capitalize the first letter of each word
const toTitleCase = (str) => {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

  const handleOpenDeleteDialog = (area) => {
    setItemToDelete(area);
    setOpenDeleteDialog(true);
  };

  const handleDeleteArea = async () => {
    try {
      await deleteDoc(doc(db, "serviceAreas", itemToDelete.id));
      setOpenDeleteDialog(false);
      setItemToDelete(null);
      setSnackbar({ open: true, message: "Service Area deleted successfully!", severity: "success" });
    } catch (error) {
      console.error("Error deleting area:", error);
      setSnackbar({ open: true, message: "Failed to delete area", severity: "error" });
    }
  };

  const handleEditOpen = (area) => {
    setSelectedArea(area);
    setOpenEditDialog(true);
  };

  const handleUpdateArea = async () => {
      const capitalizedName = toTitleCase(selectedArea.serviceAreaName.trim());

    try {
      const docRef = doc(db, "serviceAreas", selectedArea.id);
      await updateDoc(docRef, {
        serviceAreaName: capitalizedName,
        priceCategory: selectedArea.priceCategory,
        unitCode: selectedArea.unitCode,
        modifiedAt: new Date().toISOString(),
        modifiedBy: auth.currentUser?.uid || "unknown"
      });
      setOpenEditDialog(false);
      setSnackbar({ open: true, message: "Service Area updated successfully!", severity: "success" });
    } catch (error) {
      console.error("Error updating area:", error);
      setSnackbar({ open: true, message: "Failed to update area", severity: "error" });
    }
  };

  const getUnitName = (unitCode) => {
    const unit = units.find(u => u.unitCode === unitCode);
    return unit ? unit.name : "N/A";
  };

  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
    if (!companyCode) return;

    const fetchCompany = async () => {
      try {
        const snap = await getDoc(doc(db, "companies", companyCode));
        if (snap.exists()) setCompanyName(snap.data().name || "N/A");
      } catch (error) { console.error(error); }
    };
    fetchCompany();

    const fetchUnits = async () => {
      const snapshot = await getDocs(query(collection(db, "units"), 
      where("companyCode", "==", companyCode)));
      setUnits(snapshot.docs.map(doc => ({ 
        unitCode: doc.id, 
        name: doc.data().unitName || doc.id 
    })));
    };
    fetchUnits();

    const fetchPriceCategories = async () => {
  const snapshot = await getDocs(
    query(collection(db, "priceCategories"), where("companyCode", "==", companyCode))
  );
  setPriceCategories(
    snapshot.docs.map(doc => ({
      id: doc.id,
      priceCategoryName: doc.data().priceCategoryName,
      unitCode: doc.data().unitCode // include unitCode
    }))
  );
};
    fetchPriceCategories();

    const areaQuery = query(collection(db, 'serviceAreas'), where('companyCode', '==', companyCode));
    const unsubscribe = onSnapshot(areaQuery, (snap) => {
      const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setServiceAreas(list);
    });
    return () => unsubscribe();
  }, [companyCode]);

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    setNewServiceArea({ serviceAreaName: '', priceCategory: '', unitCode: '' });
  };
  const handleCloseAddDialog = () => setOpenAddDialog(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewServiceArea(prev => ({ ...prev, [name]: value }));
  };

  const handleAddArea = async () => {
    if (!newServiceArea.serviceAreaName || !newServiceArea.unitCode || !newServiceArea.priceCategory) {
      alert("Please enter service area name, unit, and select price categories");
      return;
    }

     const capitalizedName = toTitleCase(newServiceArea.serviceAreaName.trim());

    const docRef = doc(collection(db, "serviceAreas"));
    try {
      await setDoc(docRef, {
        id: docRef.id,
        serviceAreaName: /*newServiceArea.serviceAreaName.trim()*/ capitalizedName,
        unitCode: newServiceArea.unitCode,
        priceCategory: newServiceArea.priceCategory,
        companyCode,
        createdAt: new Date().toISOString(),
        createdBy: auth.currentUser?.uid || "unknown",
        modifiedAt: new Date().toISOString(),
        modifiedBy: auth.currentUser?.uid || "unknown"
      });
      setSnackbar({ open: true, message: "Service Area added successfully!", severity: "success" });
      handleCloseAddDialog();
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: "Failed to add service area", severity: "error" });
    }
  };

  const filteredAreas = serviceAreas.filter(area => area.serviceAreaName?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
     <Box sx={{ p: 4, bgcolor: 'background.default', position: 'relative' }}>
       <Typography
        variant="h4"
        sx={{ fontWeight: "bold", color: "text.primary", mb: 3 }}
      >Service Area Master</Typography>

<Box
  sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 3,
    mt:2
  }}
>
  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb:1, mt:2}}>
    <Typography variant="h5" sx={{ fontWeight: "bold", color: "text.primary" }}>
      Service Area List
    </Typography>

     <TextField
          label="Search By Service Area"
          size="small"
          sx={{ width: 300 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />
  </Box>

   <Button variant="contained" onClick={handleOpenAddDialog}>Add Service Area</Button>
</Box>
     
      <StyledCard>
       <TableContainer component={Paper} sx={{ bgcolor: "background.paper" }}>
          <Table>
            <TableHead>
               <TableRow sx={{ bgcolor: "background.default" }}>
                <TableCell  sx={{ color: "text.primary", fontWeight: "bold" }}>Unit</TableCell>
                <TableCell  sx={{ color: "text.primary", fontWeight: "bold" }}>Company</TableCell>
                <TableCell sx={{ color: "text.primary", fontWeight: "bold" }}>Service Area Name</TableCell>
                <TableCell  sx={{ color: "text.primary", fontWeight: "bold" }}>Price Category</TableCell>
                {/*<TableCell  sx={{ color: "text.primary", fontWeight: "bold" }}>Created At</TableCell>*/}
                <TableCell  sx={{ color: "text.primary", fontWeight: "bold" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAreas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                                      <Typography variant="body1" color="text.secondary">
                                         No Service Areas available.
                                      </Typography>
                                  </TableCell>
                </TableRow>
              ) : filteredAreas.map(area => (
                <TableRow key={area.id}>
                  <TableCell>{getUnitName(area.unitCode)}</TableCell>
                  <TableCell>{companyName}</TableCell>
                  <TableCell>{area.serviceAreaName}</TableCell>
                  <TableCell>{area.priceCategory}</TableCell>
                  {/*<TableCell>{area.createdAt || "N/A"}</TableCell>*/}
                  <TableCell>
                    <IconButton color="primary" onClick={() => handleEditOpen(area)}><EditIcon /></IconButton>
                    <IconButton color="error" onClick={() => handleOpenDeleteDialog(area)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </StyledCard>

      {/* Add Dialog */}
      <Dialog open={openAddDialog} /*onClose={handleCloseAddDialog}*/
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
           handleCloseAddDialog();
          }
        }}>
        <DialogTitle>Add Service Area</DialogTitle>
        <DialogContent>
           <TextField
  autoFocus
  margin="dense"
  name="serviceAreaName"
  label="Service Area Name"
  fullWidth
  value={newServiceArea.serviceAreaName}
   onChange={(e) => {
    const val = e.target.value;
    setNewServiceArea(prev => ({
      ...prev,
      serviceAreaName: toTitleCase(val)
    }));
  }}
/>
                    {/*<FormControl fullWidth margin="dense" sx={{ mt: 2, minWidth: 90 }}>
                                         <InputLabel id="unit-select-label">Unit</InputLabel>
                                         <Select
                      labelId="unit-select-label"
                      name="unitCode"
                       value={newServiceArea.unitCode || ''}
                      label="Unit"
                      required
                      onChange={handleChange}
                      MenuProps={{ PaperProps: { style: { maxHeight: 90 } } }}
                    >
                                           {units.length > 0 ? (
                                             units.map(unit => (
                                               <MenuItem key={unit.unitCode} value={unit.unitCode}>
                                                 {unit.name}
                                               </MenuItem>
                                             ))
                                           ) : (
                                             <MenuItem value="" disabled>
                                               No units available
                                             </MenuItem>
                                           )}
                                         </Select>
                                       </FormControl>*/}
          {/*<FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel>Unit</InputLabel>
            <Select
              name="unitCode"
              value={newServiceArea.unitCode || ''}
              onChange={handleChange}
            >
              {units.map(u => <MenuItem key={u.unitCode} value={u.unitCode}>{u.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense" sx={{ mt: 2, minWidth: 90 }}>
            <InputLabel>Price Categories</InputLabel>
            <Select
              multiple
              value={newServiceArea.priceCategories}
              onChange={(e) => setNewServiceArea(prev => ({ ...prev, priceCategories: e.target.value }))}
              renderValue={(selected) => selected.join(', ')}
              label="Price Categories"
            >
              {priceCategories.map(pc => (
                <MenuItem key={pc.id} value={pc.priceCategoryName}>
                  <Checkbox checked={newServiceArea.priceCategories.indexOf(pc.priceCategoryName) > -1} />
                  <ListItemText primary={pc.priceCategoryName} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>*/}
          <FormControl fullWidth margin="dense" sx={{ mt: 2, minWidth: 90 }}>
  <InputLabel id="unit-select-label">Unit</InputLabel>
  <Select
    labelId="unit-select-label"
    name="unitCode"
    value={newServiceArea.unitCode || ''}
    label="Unit"
    required
    onChange={(e) => {
      handleChange(e);
      // Reset selected price categories when unit changes
      setNewServiceArea(prev => ({ ...prev, priceCategory: '' }));
    }}
    MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
  >
    {units.length > 0 ? (
      units.map(unit => (
        <MenuItem key={unit.unitCode} value={unit.unitCode}>
          {unit.name}
        </MenuItem>
      ))
    ) : (
      <MenuItem value="" disabled>No units available</MenuItem>
    )}
  </Select>
</FormControl>
<FormControl fullWidth margin="dense" sx={{ mt: 2, minWidth: 150 }}>
  <InputLabel>Price Category</InputLabel>
  <Select
    value={newServiceArea.priceCategory || ''} // single value now
    onChange={(e) =>
      setNewServiceArea(prev => ({ ...prev, priceCategory: e.target.value }))
    }
    label="Price Category"
    disabled={!newServiceArea.unitCode} // Disable until unit is selected
  >
    {priceCategories
      .filter(pc => pc.unitCode === newServiceArea.unitCode) // filter by selected unit
      .map(pc => (
        <MenuItem key={pc.id} value={pc.priceCategoryName}>
          {pc.priceCategoryName}
        </MenuItem>
      ))}
  </Select>
</FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button onClick={handleAddArea}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
        <DialogTitle>Edit Service Area</DialogTitle>
        <DialogContent>
        <TextField
  margin="dense"
  label="Service Area Name"
  fullWidth
  value={selectedArea?.serviceAreaName || ""}
  onChange={(e) =>
    setSelectedArea(prev => ({
      ...prev,
      serviceAreaName: toTitleCase(e.target.value)
    }))
  }
/>
            <FormControl fullWidth margin="dense" sx={{ mt: 2, minWidth: 90 }}>
  <InputLabel id="unit-select-label">Unit</InputLabel>
  <Select
    labelId="unit-select-label"
    name="unitCode"
    value={newServiceArea.unitCode || ''}
    label="Unit"
    required
    onChange={(e) => {
      handleChange(e);
      // Reset selected price categories when unit changes
      setNewServiceArea(prev => ({ ...prev, priceCategory: '' }));
    }}
    MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
  >
    {units.length > 0 ? (
      units.map(unit => (
        <MenuItem key={unit.unitCode} value={unit.unitCode}>
          {unit.name}
        </MenuItem>
      ))
    ) : (
      <MenuItem value="" disabled>No units available</MenuItem>
    )}
  </Select>
</FormControl>
         <FormControl fullWidth margin="dense" sx={{ mt: 2 , minWidth: 150 }}>
  <InputLabel>Price Category</InputLabel>
  <Select
    value={selectedArea?.priceCategory || ''}
    onChange={(e) => setSelectedArea({ ...selectedArea, priceCategory: e.target.value })}
    label="Price Category"
    disabled={!selectedArea?.unitCode}
  >
    {priceCategories
      .filter(pc => pc.unitCode === selectedArea?.unitCode)
      .map(pc => (
        <MenuItem key={pc.id} value={pc.priceCategoryName}>
          {pc.priceCategoryName}
        </MenuItem>
      ))}
  </Select>
</FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateArea}>Update</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete "{itemToDelete?.serviceAreaName}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteArea} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

<Snackbar
  open={snackbar.open}
  autoHideDuration={3000}
  onClose={() => setSnackbar({ ...snackbar, open: false })}
  anchorOrigin={{ vertical: "top", horizontal: "right" }}
>
<Alert
  severity={snackbar.severity}
  variant="outlined"
  onClose={() => setSnackbar({ ...snackbar, open: false })}
  sx={{
    backgroundColor: "#fff",
    color: "#000",
    border: "1px solid #ddd",
    fontWeight: "bold",
    fontSize: "16px"
  }}
>
  {snackbar.message}
</Alert>
</Snackbar>
      
    </Box>
  );
};

export default AddServiceArea;